const {
  default: SelectInstantAppointment
} = require('../InstantPacket/components/SelectInstantAppointment');

export default function useDocActions({
  data,
  options,
  patientId,
  practice,
  setViewDocument,
  setIsEditOpen,
  handleConfirmationFile,
  setPatientSelection,
  setSelectedAppointment,
  setSendFaxModal,
  setShareWithPracticeModal,
  isLocked,
  hasPermissions,
  downloadDocument,
  cat
}) {
  const getDeleteData = (data) => ({
    mode: 'delete',
    id: data?.document_handle || data?.id,
    document_handle: data?.document_handle,
    form_id: data?.form_id,
    name: data?.name,
    appointment_id: data?.appointment_id,
    type: data?.kind,
    patient_id: data?.patient_id || data?.user_id,
    category: cat || null
  });

  const getDefaultData = (data, form) => ({
    ...data,
    load: true,
    title: data?.name,
    handle: data?.document_handle || form,
    type: data.kind === 'fax' ? 'fax' : data?.mimetype || 'form',
    id: data?.id,
    category: cat,
    appointment_id: data?.appointment_id,
    patient_id: data?.patient_id || data?.user_id,
    uploader_id: data?.uploader_id
  });

  const viewDoc = {
    label: data?.form_id || isLocked || !hasPermissions ? 'View' : 'View & Edit',
    onClick: () => setViewDocument(getDefaultData(data, null, null)),
    icon: 'eye-mono',
    permission: !options?.isPatient && { requiredPermissions: ['patient_documents_center.read'] }
  };

  const editDoc = {
    label: 'Edit',
    onClick: () => {
      setIsEditOpen(true);
      setViewDocument({
        ...getDefaultData(data, {
          ...data,
          form_id: data?.form_id,
          form_json: data?.form_json,
          form_version: data?.form_version
        }),
        action: 'Edit'
      });
    },
    icon: 'edit-2-mono',
    permission: { requiredPermissions: ['patient_documents_center.update'] }
  };

  const deleteDoc = {
    label: 'Delete',
    permission: !options?.isPatient && {
      requiredPermissions: ['patient_documents_center.delete']
    },
    onClick: () => handleConfirmationFile(getDeleteData(data, null)),
    icon: 'trash-mono'
  };

  const deleteInNote = {
    label: 'Delete',
    icon: 'trash-mono',
    onClick: () => {},
    children: [
      {
        label: 'Remove from note',
        onClick: () => handleConfirmationFile(getDeleteData(data, null)),
        icon: 'trash-mono'
      },
      {
        label: 'Delete all instances',
        onClick: () =>
          handleConfirmationFile({ ...getDeleteData(data, null), deleteInstances: true }),
        icon: 'trash-mono'
      }
    ]
  };

  const downloadDoc = {
    label: 'Download',
    onClick: downloadDocument,
    icon: 'file-download-mono',
    permission: !options?.isPatient && { requiredPermissions: ['patient_documents_center.read'] }
  };

  const assignPatient = {
    label: 'Assign patient',
    onClick: () => {
      setPatientSelection({
        show: true,
        id: data?.id,
        type: data?.form_id ? 'form' : 'document',
        patient: data?.patient,
        appointment: data?.appointment_id,
        action: 'link_patient'
      });
    },
    icon: 'user-2-mono',
    permission: { requiredPermissions: ['patient_documents_center.update'] }
  };

  const appointmentLinking = data?.appointment_id
    ? {
        label: 'Linked DOS',
        icon: 'link-linear',
        color: 'primary',
        stroke: true,
        permission: { requiredPermissions: ['patient_documents_center.update'] },
        children: [
          {
            component: (
              <SelectInstantAppointment
                selectedAppointment={data?.appointment_id}
                setSelectedAppointment={async (appointment) => {
                  await setSelectedAppointment(appointment, data);
                }}
                patientId={patientId}
                practice={practice}
                update
                buttonClassName="hover:bg-primary-50 w-36"
                popoverClassName="left-0 top-0 translate-x-[-101%] translate-y-[-30%]"
              />
            )
          },
          {
            label: 'Unlink DOS',
            icon: 'close-circle-mono',
            color: 'primary',
            iconSize: 184,
            onClick: async () => {
              await setSelectedAppointment(null, data);
            }
          }
        ]
      }
    : {
        component: (
          <SelectInstantAppointment
            selectedAppointment={data?.appointment_id}
            setSelectedAppointment={async (appointment) => {
              await setSelectedAppointment(appointment, data);
            }}
            patientId={patientId}
            practice={practice}
            popoverClassName="left-0 top-0 translate-x-[-101%] translate-y-[-90%]"
          />
        ),
        permission: { requiredPermissions: ['patient_documents_center.update'] }
      };

  const sendFax = {
    label: 'Send as fax',
    icon: 'send-mono',
    onClick: () => {
      setSendFaxModal({ show: true, files: [data] });
    }
  };

  const shareWithPractice = {
    label: 'Share with other locations',
    icon: 'share-mono',
    onClick: () => {
      setShareWithPracticeModal({ show: true, files: [data] });
    }
  };

  const shareWithPatient = {
    label: data?.myportal ? 'Unshare document' : 'Share with patient',
    icon: 'patient-response-mono',
    permission: {
      requiredPermissions: ['patient_documents_center.update', 'patient_communication.create']
    },
    onClick: () => {
      handleConfirmationFile({
        mode: data?.myportal ? 'unshare' : 'share',
        id: data?.id,
        type: data?.form_id ? 'form' : 'document'
      });
    }
  };

  return {
    viewDoc,
    editDoc,
    deleteDoc,
    deleteInNote,
    downloadDoc,
    assignPatient,
    appointmentLinking,
    sendFax,
    shareWithPractice,
    shareWithPatient
  };
}
